import { Collapse, Link, List } from '@mui/material';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAnalytics } from 'src/hooks/useAnalytics';

import { getActive, isExternalLink } from '..';
import { NavListProps } from '../type';
import NavItem from './NavItem';

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChildren: boolean;
  isCollapse?: boolean;
};

export default function NavList({
  data,
  depth,
  hasChildren,
  isCollapse = false,
}: NavListRootProps) {
  const navigate = useNavigate();
  const { sendAnalyticsEvent } = useAnalytics();

  const { pathname } = useLocation();

  const active = getActive(data.path, pathname);

  const [open, setOpen] = useState(active);

  const handleClickItem = () => {
    if (data.analyticsEvent) sendAnalyticsEvent(data.analyticsEvent);
    if (data.openInNewTab && !hasChildren) {
      // Inside the click handler on purpose: browsers only allow a new tab
      // while the user's click is still being handled.
      window.open(data.path, '_blank', 'noopener');
      return;
    }
    if (!hasChildren) {
      navigate(data.path);
    }
    setOpen(!open);
  };

  return (
    <>
      {isExternalLink(data.path) ? (
        <Link href={data.path} target="_blank" rel="noopener" underline="none">
          <NavItem
            item={data}
            depth={depth}
            open={open}
            active={active}
            isCollapse={isCollapse}
          />
        </Link>
      ) : (
        <>
          {data.shouldRender && (
            <NavItem
              item={data}
              depth={depth}
              open={open}
              active={active}
              isCollapse={isCollapse}
              onClick={handleClickItem}
            />
          )}
        </>
      )}

      {!isCollapse && hasChildren && (
        <Collapse in={open} unmountOnExit>
          <List component="div" disablePadding>
            <NavSubList data={data.children} depth={depth} />
          </List>
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

type NavListSubProps = {
  data: NavListProps[];
  depth: number;
};

function NavSubList({ data, depth }: NavListSubProps) {
  return (
    <>
      {data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChildren={!!list.children}
        />
      ))}
    </>
  );
}
